import { EtaDomRenderer } from "@/controllers/eta-renderer/eta-dom-renderer";
import { EtaMutationObserver } from "@/controllers/eta-renderer/eta-mutation-observer";
import { EtaTemplateCache } from "@/controllers/eta-renderer/eta-template-cache";
import { EtaRenderHost } from "@/controllers/eta-renderer/eta-types";
import { Task } from "@lit/task";
import { ReactiveController } from "lit";

export type { EtaRenderHost } from "@/controllers/eta-renderer/eta-types";

export class EtaRenderController implements ReactiveController {
  private host          : EtaRenderHost;
  private _etaRenderTask: Task<unknown[], unknown>;

  private _cache = new EtaTemplateCache();
  private _observer: EtaMutationObserver;
  private _renderer: EtaDomRenderer;

  private _renderInProgress = false;
  private _etaIdleWaiters: Array<() => void> = [];
  private _mutationRequested = false;

  constructor(host: EtaRenderHost) {
    this.host = host;

    this._observer = new EtaMutationObserver(host, this._cache, () => this._scheduleMutationRender());
    this._renderer = new EtaDomRenderer(host, this._cache, (sr, h) => this._observer.ensureShadowRootObserved(sr, h));

    host.addController(this);

    this._etaRenderTask = new Task(host, {
      task: async () => {
        try {
          if (!this.host.shouldRunEtaStateRender()) return;
          const fromMutation = this._mutationRequested;

          const ctx = await this.host.buildEtaContext();
          if (!ctx) return;

          if (fromMutation) this._mutationRequested = false;

          const dirtyRoots = fromMutation ? this._observer.consumeDirtyRoots() : undefined;

          this._renderInProgress = true;
          try {
            await this._observer.suspend(async () => {
              await this._renderer.render(ctx, { fromMutation, dirtyRoots });
            });
          } finally {
            this._renderInProgress = false;
          }
        } finally {
          this._notifyEtaIdle();
        }
      },
      args: () => this.host.getEtaArgs()
    });
  }

  get taskComplete() {
    return this._etaRenderTask.taskComplete;
  }

  waitForIdle(): Promise<void> {
    if (this._isEtaIdle()) return Promise.resolve();
    return new Promise<void>((resolve) => {
      this._etaIdleWaiters.push(resolve);
    });
  }

  hostConnected() {
    this._observer.connect();
  }

  hostDisconnected() {
    this._observer.disconnect();
  }

  private _scheduleMutationRender() {
    this._mutationRequested = true;
    this._etaRenderTask.run();
  }

  private _isEtaIdle(): boolean {
    return !this._renderInProgress && this._observer.isIdle();
  }

  private _notifyEtaIdle() {
    if (!this._isEtaIdle()) return;
    if (this._etaIdleWaiters.length === 0) return;
    const waiters = this._etaIdleWaiters.splice(0);
    for (const w of waiters) w();
  }
}
